import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { getPaymentSummary } from "features/Payments/services/PaymentService";
import type { PaymentSummaryData } from "features/Payments/types/payment.types";
// import {
//   PaymentDirection,
//   PaymentSourceLabelMap,
//   PaymentSourceType,
//   paymentDirectionMap,
// } from "features/Payments/constants/payment.constants";
import { formatCurrency } from "utils/currency";
import { DateOnly } from "components/common/Date";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// function sourceTypeLabel(sourceType: string): string {
//   const v = sourceType as PaymentSourceType;
//   if (v in PaymentSourceLabelMap) {
//     return PaymentSourceLabelMap[v];
//   }
//   return sourceType.replace(/_/g, " ");
// }

// function directionLabel(direction: string): string {
//   const d = direction as PaymentDirection;
//   if (d === PaymentDirection.IN || d === PaymentDirection.OUT) {
//     return paymentDirectionMap[d].label;
//   }
//   return direction;
// }

export default function PaymentSummaryWidgets() {
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<PaymentSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    const out: number[] = [];
    for (let i = y - 5; i <= y + 2; i += 1) out.push(i);
    return out;
  }, [now]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPaymentSummary({ year, month });
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setData(null);
        setError(res.message || "Could not load payment summary.");
      }
    } catch (err) {
      setData(null);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not load payment summary.";
      setError(
        typeof msg === "string" ? msg : "Could not load payment summary.",
      );
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = data?.totals;
  // const rows = data?.bySourceTypeAndDirection ?? [];
  const period = data?.period;

  return (
    <div className="mb-4">
      <Row className="g-3 align-items-end mb-3">
        <Col xs="auto">
          <Label className="form-label text-muted small mb-1 d-block">
            Summary period
          </Label>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Input
              type="select"
              bsSize="sm"
              className="w-auto"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              aria-label="Month"
            >
              {MONTH_LABELS.map((label, i) => (
                <option key={label} value={i + 1}>
                  {label}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              bsSize="sm"
              className="w-auto"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              aria-label="Year"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Input>
          </div>
        </Col>
        <Col className="text-muted small">
          {period?.start && period?.end ? (
            <>
              Posted payments with{" "}
              <span className="fw-medium">payment date</span> from{" "}
              <DateOnly date={period.start} /> to <DateOnly date={period.end} />
            </>
          ) : null}
        </Col>
      </Row>

      {error ? (
        <Alert color="danger" className="mb-0">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted py-4">
          <Spinner size="sm" />
          <span>Loading summary…</span>
        </div>
      ) : data && totals ? (
        <>
          <Row className="g-3 mb-3">
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Received (IN)
                  </p>
                  <h4 className="mb-0 text-success tabular-nums">
                    {formatCurrency(totals.postedInAmount, { compact: true })}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Paid (OUT)
                  </p>
                  <h4 className="mb-0 text-danger tabular-nums">
                    {formatCurrency(totals.postedOutAmount, { compact: true })}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Net
                  </p>
                  <h4
                    className={`mb-0 tabular-nums ${
                      totals.postedNetAmount > 0
                        ? "text-success"
                        : totals.postedNetAmount < 0
                          ? "text-danger"
                          : "text-body"
                    }`}
                  >
                    {formatCurrency(totals.postedNetAmount, { compact: true })}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Transactions
                  </p>
                  <h4 className="mb-0 tabular-nums">
                    {totals.postedTransactionCount.toLocaleString("en-IN")}
                  </h4>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* <Card className="border shadow-none">
            <CardBody className="py-3">
              <h6 className="text-muted text-uppercase small mb-3">
                By source &amp; direction
              </h6>
              {rows.length === 0 ? (
                <p className="text-muted mb-0 small">
                  No posted payments in this period.
                </p>
              ) : (
                <div className="table-responsive mb-0">
                  <Table size="sm" className="align-middle mb-0">
                    <thead className="table-light text-muted small">
                      <tr>
                        <th scope="col">Source</th>
                        <th scope="col">Direction</th>
                        <th scope="col" className="text-end">
                          Amount
                        </th>
                        <th scope="col" className="text-end">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={`${r.sourceType}-${r.direction}-${i}`}>
                          <td>{sourceTypeLabel(r.sourceType)}</td>
                          <td>{directionLabel(r.direction)}</td>
                          <td className="text-end tabular-nums fw-medium">
                            {formatCurrency(r.totalAmount)}
                          </td>
                          <td className="text-end tabular-nums text-muted">
                            {r.transactionCount.toLocaleString("en-IN")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card> */}
        </>
      ) : null}
    </div>
  );
}
